import { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useGeoapifyAddressAutocomplete } from '../../hooks/useGeoapifyAddressAutocomplete';
import type { StrukturierteAdresse } from '../../lib/api/stammdaten';
import { cn } from '../../lib/utils';

const LAENDER = [
  { code: 'DE', name: 'Deutschland' },
  { code: 'AT', name: 'Österreich' },
  { code: 'CH', name: 'Schweiz' },
];

type Props = Readonly<{
  wert: StrukturierteAdresse;
  onChange: (adresse: StrukturierteAdresse) => void;
  /** Blendet das Feld „Land" ein — der Standort-Admin entscheidet das. */
  landSichtbar?: boolean;
  fehlend?: string[];
  disabled?: boolean;
}>;

/**
 * Adresse mit Suche und Einzelfeldern.
 *
 * Die Suche zerlegt der Hook bereits in Straße, Hausnummer, PLZ, Ort und Land;
 * vorher parste jedes Formular den formatierten Text noch einmal selbst und kam
 * dabei auf andere Ergebnisse.
 */
export default function AdressFelder({ wert, onChange, landSichtbar = false, fehlend = [], disabled = false }: Props) {
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState(false);
  const { suggestions, apiKeyPresent, isLoading, providerFailed } = useGeoapifyAddressAutocomplete({
    query: suche,
    enabled: !disabled,
  });

  const fehlt = (feld: string) => fehlend.includes(feld);
  const fehlerKlasse = 'border-destructive focus-visible:ring-destructive/40';
  const setzen = (teil: Partial<StrukturierteAdresse>) => onChange({ ...wert, ...teil });

  return (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label htmlFor="adresse-suche">Adresse suchen</Label>
        <div className="relative">
          <Input
            id="adresse-suche"
            autoComplete="off"
            disabled={disabled}
            placeholder="Straße und Hausnummer eingeben…"
            value={suche}
            onChange={(e) => {
              setSuche(e.target.value);
              setOffen(true);
            }}
            onFocus={() => setOffen(true)}
            onBlur={() => window.setTimeout(() => setOffen(false), 120)}
          />
          {offen && suggestions.length > 0 ? (
            <div className="absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-md border bg-background shadow-lg">
              {suggestions.map((vorschlag) => (
                <button
                  key={vorschlag.id}
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const s = vorschlag.structured;
                    onChange({
                      street: [s.street, s.houseNumber].filter(Boolean).join(' ').trim(),
                      zip: s.postalCode || s.zip || '',
                      city: s.city || '',
                      country: s.country || wert.country || 'DE',
                      formatted: s.formatted || vorschlag.label,
                    });
                    setSuche(s.formatted || vorschlag.label);
                    setOffen(false);
                  }}
                >
                  {vorschlag.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {!apiKeyPresent ? (
          <p className="text-[11px] text-muted-foreground">
            Kein Adress-API-Schlüssel hinterlegt — die Felder unten von Hand ausfüllen.
          </p>
        ) : null}
        {apiKeyPresent && providerFailed ? (
          // Vorher blieb das Feld bei einem abgelehnten Schlüssel wortlos leer.
          <p className="text-[11px] text-destructive">
            Die Adress-Suche antwortet nicht — bitte den Schlüssel in den Einstellungen
            prüfen. Die Felder unten lassen sich von Hand ausfüllen.
          </p>
        ) : null}
        {isLoading ? <p className="text-[11px] text-muted-foreground">Suche läuft…</p> : null}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="adresse-strasse">Straße und Hausnummer</Label>
        <Input
          id="adresse-strasse"
          disabled={disabled}
          className={cn(fehlt('Straße') && fehlerKlasse)}
          value={wert.street || ''}
          onChange={(e) => setzen({ street: e.target.value })}
        />
      </div>

      <div className={cn('grid gap-3', landSichtbar ? 'md:grid-cols-3' : 'md:grid-cols-2')}>
        <div className="grid gap-1.5">
          <Label htmlFor="adresse-plz">PLZ</Label>
          <Input
            id="adresse-plz"
            disabled={disabled}
            className={cn(fehlt('PLZ') && fehlerKlasse)}
            value={wert.zip || wert.postalCode || ''}
            onChange={(e) => setzen({ zip: e.target.value, postalCode: undefined })}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="adresse-ort">Ort</Label>
          <Input
            id="adresse-ort"
            disabled={disabled}
            className={cn(fehlt('Ort') && fehlerKlasse)}
            value={wert.city || ''}
            onChange={(e) => setzen({ city: e.target.value })}
          />
        </div>
        {landSichtbar ? (
          <div className="grid gap-1.5">
            <Label htmlFor="adresse-land">Land</Label>
            <select
              id="adresse-land"
              disabled={disabled}
              className="h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
              value={wert.country || 'DE'}
              onChange={(e) => setzen({ country: e.target.value })}
            >
              {LAENDER.map((land) => (
                <option key={land.code} value={land.code}>{land.name}</option>
              ))}
            </select>
          </div>
        ) : null}
      </div>
    </div>
  );
}
